/**
 * Карточка участка — разметка 1:1 из legacy htdocs/core/templates/admin_area.tpl
 * (82 КБ: вёрстка + inline-JS, который собирал таблицы и модалки).
 *
 * Соответствие legacy (классы и порядок блоков сохранены):
 *   .areabackbtn / h2.sub-header + .areainfonbm — «Назад» и «Настройки участка №N, M»
 *   .curareaid[data-areaid] / .modalsblock      — служебные диве шаблона
 *   .areainfo > table.areainfotable             — номер, л/с, площадь, ставка, баланс,
 *                                                 логин, пароль, автооплата (label.switch)
 *   .maincontact / .contactslist                — основной и доп. контакты (clhead/clbody)
 *   .counterslist / .avtoplateslist             — счётчики и номера автомобилей
 *   .invoicesslist / .historylist               — квитанции и история по счёту (nclhead/nclbody)
 *   .clcollapse + .collapsespan                 — сворачивание блоков (при загрузке ВСЕ свёрнуты —
 *                                                 так отрабатывал init-цикл clcollapse в шаблоне)
 *   модалки: .addnewusermodal(bk) / .addnewcountermodal(bk) / .addnewsyncareamodal(bk) /
 *            .avtoplatemodal(bk) / .removemodal(bk) / .alertPaymentModal
 *            — окна свои, не бутстраповские, поэтому общий components/Modal.tsx здесь не
 *              используется; разметка legacy, а поведение (фон / Esc / блокировка прокрутки)
 *              вынесено в локальный useModalBehavior — см. комментарий у хука.
 *
 * jQuery-плагинов нет: bootstrap-select заменён обычным <select> (классы те же),
 * маски jquery.maskedinput («+7 (999) 999-99-99», «~999~~») — обычные input'ы.
 *
 * API (Handlers\Admin\Areas): GET /api/admin/areas/{id} — вся карточка одним запросом
 * (legacy тянул её getInfo + usersarealist + countersarealist + avtoplatelist +
 * getSyncesAreas + invoceslist + paymentshistorylist + checksetautoinvoicespay);
 * мутации — PUT /{id}, POST /{id}/autopay, контакты/счётчики/номера/sync,
 * GET /{id}/invoices/{invId}/after-payment + POST …/pay.
 */

import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useParams } from 'react-router-dom'

import { api, ApiError } from '@/api/client'
import type { PgBool } from '@/api/types'
import { isTrue, toNumber } from '@/lib/format'
import { useApi } from '@/lib/useApi'

// ─── Ответы API ──────────────────────────────────────────────────────────────

interface AreaInfo {
  id: number
  name: string
  area_pid: number | null
  land_area: string | null
  land_area_rate: string
  money: string
  login: string | null
  password: string | null
  invoiceautopay: PgBool
}

interface ContactRow {
  id: number
  familia: string | null
  imuy: string | null
  otchestvo: string | null
  tel: string | null
  email: string | null
  rassl_sms: string | null
  rassl_email: string | null
  candidate: string | null
  isowner: PgBool
}

interface CounterRow {
  id: number
  number: string
}

interface PlateRow {
  id: number
  avtoplatenumber: string
  avtoplateregion: string
}

interface SyncRow {
  id: number
  name: string
}

interface InvoiceRow {
  smsid: number
  typeinvoice: string
  totalcost: string
  areaid: number | null
  areaname: string | null
  waspaid: PgBool
  oldinv: PgBool
  date_end: string | null
  invoce_date_add: string | null
}

interface TransactionRow {
  id: number
  sum: string
  time: string | null
  name: string | null
  areaname: string | null
}

/** GET /api/admin/areas/{id} (Areas::show). */
interface AreaCard {
  area: AreaInfo
  owner: ContactRow | null
  contacts: ContactRow[]
  counters: CounterRow[]
  plates: PlateRow[]
  synced: SyncRow[]
  invoices: InvoiceRow[]
  transactions: TransactionRow[]
  unpaid_count: number
  autopay: boolean
}

/** GET /api/admin/areas/{id}/invoices/{invId}/after-payment (legacy afterpayment). */
interface AfterPayment {
  rest: string
  can_pay: boolean
}

// ─── Состояние модалок ───────────────────────────────────────────────────────

type Modal =
  | { kind: 'none' }
  | { kind: 'editInfo'; type: string }
  | { kind: 'syncArea'; candidates: SyncRow[] }
  | { kind: 'confirmSyncDel'; syncId: number }
  | { kind: 'addCounter'; free: CounterRow[] }
  | { kind: 'confirmCounterDel'; counterId: number }
  | { kind: 'contactForm'; title: string; owner: boolean; userId: number | null }
  | { kind: 'confirmContactDel'; userId: number }
  | { kind: 'contactDelBlocked' }
  | { kind: 'plateForm'; plateId: number | null }
  | { kind: 'confirmPlateDel'; plateId: number }
  | { kind: 'pay'; invoice: InvoiceRow; datee: string; rest: string; canPay: boolean }

interface ContactForm {
  familia: string
  imuy: string
  otchestvo: string
  tel: string
  email: string
  sms: boolean
  emailRassl: boolean
  candidate: boolean
}

const EMPTY_CONTACT: ContactForm = {
  familia: '',
  imuy: '',
  otchestvo: '',
  tel: '',
  email: '',
  sms: false,
  emailRassl: false,
  candidate: false,
}

/** legacy invoicetype — подписи именно этого шаблона (строчные). */
const INVOICE_TYPE: Record<string, string> = {
  electro: 'электроэнергия',
  chlenskie: 'членский взнос',
  celevie: 'целевой взнос',
  other: 'прочее',
}

type Section = 'maincontact' | 'contacts' | 'counters' | 'plates' | 'invoices' | 'history'

const pad = (value: number): string => (value < 10 ? `0${value}` : String(value))

/** legacy date_normalize: «2024-03-15 10:00:00» → «15.03.2024 ». */
function dateNormalize(value: string | null): string {
  if (value === null || value === '') return ''
  const date = new Date(value.replace(' ', 'T'))
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} `
}

/** legacy date_normalize_with_time: «15.03.2024 10:00:00». */
function dateNormalizeWithTime(value: string | null): string {
  if (value === null || value === '') return ''
  const date = new Date(value.replace(' ', 'T'))
  return (
    `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  )
}

/** legacy: дата квитанции — date_end у электро, дата выставления у остальных. */
function invoiceDate(invoice: InvoiceRow): string {
  return invoice.typeinvoice === 'electro'
    ? (invoice.date_end ?? '')
    : dateNormalize(invoice.invoce_date_add)
}

/** legacy: familia/imuy/otchestvo с подменой null на пустую строку. */
function fio(row: ContactRow): string {
  return `${row.familia ?? ''} ${row.imuy ?? ''} ${row.otchestvo ?? ''}`
}

/**
 * Поведение legacy-модалок карточки участка.
 *
 * Общий <Modal> (components/Modal.tsx) сюда не подходит: окна здесь не бутстраповские,
 * а собственные (.removemodal / .addnewusermodal / .addnewcountermodal / .addnewsyncareamodal /
 * .avtoplatemodal / .alertPaymentModal со своими .admtitle/.admbody/.admfoot, стили в admin.css) —
 * перевод на .modal-dialog поменял бы вид. Поэтому разметка остаётся legacy, а недостающее
 * поведение добираем здесь:
 *   • Esc закрывает окно (в legacy закрывали только крестик и клик по фону);
 *   • пока окно открыто, страница под ним не прокручивается.
 *
 * Клик по затемнению уже закрывает окно (onClick на .*modalbk), а клик внутри окна его не
 * закрывает: окно — сосед подложки, а не её потомок, всплывать до неё некуда. На всякий случай
 * обработчик подложки дополнительно проверяет event.target === event.currentTarget.
 *
 * Исключение — modal.kind === 'pay' (.alertPaymentModal): у него подложки нет и в legacy
 * (в admin.css это просто fixed-окно, класса .alertPaymentModalbk не существует), поэтому
 * закрывать по фону там нечего — окно закрывается крестиком и Esc. Вид сохранён как в legacy;
 * добавлять затемнение — отдельное решение по дизайну, а не задача этого рефакторинга.
 */
function useModalBehavior(open: boolean, onClose: () => void): void {
  // onClose пересоздаётся на каждый рендер — держим его в ref, чтобы эффект
  // зависел только от open (иначе на каждом рендере он бы снимал/ставил блокировку
  // прокрутки и запоминал уже подменённое значение overflow).
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return

    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = overflow
    }
  }, [open])
}

export function AdminAreaPage() {
  const { id } = useParams<{ id: string }>()
  const areaId = Number(id ?? 0)

  const { data, reload } = useApi<AreaCard>(`/api/admin/areas/${areaId}`)

  const [modal, setModal] = useState<Modal>({ kind: 'none' })
  const [contact, setContact] = useState<ContactForm>(EMPTY_CONTACT)
  const [plate, setPlate] = useState<{ number: string; region: string }>({ number: '', region: '' })
  const [infoValue, setInfoValue] = useState('')
  const [selected, setSelected] = useState('0')
  const [failure, setFailure] = useState<string | null>(null)

  // legacy: init-цикл прогоняет clcollapse по всем .clcollapse → все блоки свёрнуты
  const [collapsed, setCollapsed] = useState<Record<Section, boolean>>({
    maincontact: true,
    contacts: true,
    counters: true,
    plates: true,
    invoices: true,
    history: true,
  })

  useEffect(() => {
    setModal({ kind: 'none' })
    setFailure(null)
  }, [areaId])

  const close = (): void => {
    setModal({ kind: 'none' })
    setFailure(null)
  }

  // Esc + блокировка прокрутки для всех модалок карточки (см. useModalBehavior).
  useModalBehavior(modal.kind !== 'none', close)

  /** Закрываем только по клику в саму подложку, а не по клику, всплывшему из окна. */
  const onBackdropClick = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) close()
  }

  /** Общая обёртка: запрос → перезагрузка карточки (legacy после каждой правки звал init*). */
  async function run(action: () => Promise<void>): Promise<void> {
    try {
      setFailure(null)
      await action()
      reload()
    } catch (e: unknown) {
      setFailure(e instanceof ApiError ? e.message : 'Ошибка запроса')
    }
  }

  if (data == null) {
    return (
      <>
        <div onClick={() => window.history.go(-1)} className="areabackbtn">
          Назад
        </div>
        <h2 className="sub-header">
          Настройки участка №<span className="areainfonbm" />
        </h2>
        <div className="curareaid" data-areaid={areaId} />
        <div className="modalsblock" />
      </>
    )
  }

  const { area, owner, contacts, counters, plates, synced, invoices, transactions } = data
  const hasOwner = owner !== null

  // legacy getInfo: «номер участка» = имя участка + имена связанных через запятую
  const areaNbm = area.name + synced.map((row) => `, ${row.name}`).join('')

  const toggleSection = (section: Section): void =>
    setCollapsed((prev) => ({ ...prev, [section]: !prev[section] }))

  const bodyStyle = (section: Section): { display?: string } =>
    collapsed[section] ? { display: 'none' } : {}

  const headStyle = (section: Section): { borderBottom: string } => ({
    borderBottom: collapsed[section] ? '1px solid #ccc' : '0px',
  })

  const collapseIcon = (section: Section) => (
    <span className="collapsespan">
      <i className={collapsed[section] ? 'fas fa-angle-double-down' : 'fas fa-angle-double-up'} />
    </span>
  )

  // ── Действия (legacy-обработчики) ─────────────────────────────────────────

  /** legacy .areancontroledit → getcurentinfo → модалка «Редактирование». */
  function openEditInfo(type: string): void {
    const current: Record<string, string> = {
      infoland: area.land_area ?? '',
      // в БД ставка лежит без пробелов и с точкой — снимаем формат number_format
      infolandrate: String(toNumber(area.land_area_rate)),
      infologin: area.login ?? '',
      infopassword: area.password ?? '',
    }
    setInfoValue(current[type] ?? '')
    setModal({ kind: 'editInfo', type })
  }

  const saveInfo = (type: string): Promise<void> =>
    run(async () => {
      await api.put(`/api/admin/areas/${areaId}`, { type, value: infoValue })
      close()
    })

  /** legacy .syncareaadd → area_list(areasync) → модалка «Прикрепить участок». */
  const openSyncArea = (): Promise<void> =>
    run(async () => {
      const candidates = await api.get<SyncRow[]>(`/api/admin/areas/${areaId}/sync/candidates`)
      setSelected('0')
      setModal({ kind: 'syncArea', candidates })
    })

  const addSyncArea = (): Promise<void> =>
    run(async () => {
      await api.post(`/api/admin/areas/${areaId}/sync`, { syncarea: selected })
      close()
    })

  const delSyncArea = (syncId: number): Promise<void> =>
    run(async () => {
      await api.delete(`/api/admin/areas/${areaId}/sync/${syncId}`)
      close()
    })

  /** legacy .clhcounteradd → counterslist → модалка «Добавить счетчик». */
  const openAddCounter = (): Promise<void> =>
    run(async () => {
      const free = await api.get<CounterRow[]>(`/api/admin/areas/${areaId}/counters/free`)
      setSelected(free.length > 0 ? String(free[0].id) : '')
      setModal({ kind: 'addCounter', free })
    })

  const addCounter = (): Promise<void> =>
    run(async () => {
      await api.post(`/api/admin/areas/${areaId}/counters`, { counterid: selected })
      close()
    })

  const delCounter = (counterId: number): Promise<void> =>
    run(async () => {
      await api.delete(`/api/admin/areas/${areaId}/counters/${counterId}`)
      close()
    })

  function openAddContact(asOwner: boolean): void {
    setContact(EMPTY_CONTACT)
    setModal({
      kind: 'contactForm',
      title: asOwner ? 'Добавить собственника' : 'Добавить контакт',
      owner: asOwner,
      userId: null,
    })
  }

  /** legacy .contactedit → showuser → модалка «Редактировать контакт». */
  const openEditContact = (userId: number, notOwner: boolean): Promise<void> =>
    run(async () => {
      const row = await api.get<ContactRow>(`/api/admin/areas/${areaId}/contacts/${userId}`)
      setContact({
        familia: row.familia ?? '',
        imuy: row.imuy ?? '',
        otchestvo: row.otchestvo ?? '',
        tel: row.tel ?? '',
        email: row.email ?? '',
        sms: row.rassl_sms === '1',
        emailRassl: row.rassl_email === '1',
        candidate: row.candidate === '1',
      })
      setModal({ kind: 'contactForm', title: 'Редактировать контакт', owner: !notOwner, userId })
    })

  /** Поля контакта под legacy-именами; чекбоксы уходят строками 'true'/'false'. */
  const contactPayload = (): Record<string, string> => ({
    adminputfamilia: contact.familia,
    adminputimuy: contact.imuy,
    adminputotchestvo: contact.otchestvo,
    adminputtel: contact.tel,
    adminputemail: contact.email,
    admcheckboxsms: String(contact.sms),
    admcheckboxemail: String(contact.emailRassl),
    admcheckboxcandidate: String(contact.candidate),
  })

  const addContact = (asOwner: boolean): Promise<void> =>
    run(async () => {
      await api.post(`/api/admin/areas/${areaId}/contacts`, {
        ...contactPayload(),
        typeadd: asOwner ? 'addowner' : '',
      })
      close()
    })

  const editContact = (userId: number): Promise<void> =>
    run(async () => {
      await api.put(`/api/admin/areas/${areaId}/contacts/${userId}`, contactPayload())
      close()
    })

  /**
   * legacy .contactremove (admin_area.tpl:1223): основной контакт нельзя удалить,
   * пока есть другие контакты, счётчики или номера автомобилей.
   */
  function openDelContact(userId: number, type: 'main' | 'other'): void {
    const blocked = type === 'main' && contacts.length + counters.length + plates.length > 0
    setModal(blocked ? { kind: 'contactDelBlocked' } : { kind: 'confirmContactDel', userId })
  }

  const delContact = (userId: number): Promise<void> =>
    run(async () => {
      await api.delete(`/api/admin/areas/${areaId}/contacts/${userId}`)
      close()
    })

  function openAddPlate(): void {
    setPlate({ number: '', region: '' })
    setModal({ kind: 'plateForm', plateId: null })
  }

  const openEditPlate = (plateId: number): Promise<void> =>
    run(async () => {
      const row = await api.get<PlateRow>(`/api/admin/areas/${areaId}/plates/${plateId}`)
      setPlate({ number: row.avtoplatenumber, region: row.avtoplateregion })
      setModal({ kind: 'plateForm', plateId })
    })

  const savePlate = (plateId: number | null): Promise<void> =>
    run(async () => {
      const payload = { platenumber: plate.number, plateregion: plate.region }
      if (plateId === null) await api.post(`/api/admin/areas/${areaId}/plates`, payload)
      else await api.put(`/api/admin/areas/${areaId}/plates/${plateId}`, payload)
      close()
    })

  const delPlate = (plateId: number): Promise<void> =>
    run(async () => {
      await api.delete(`/api/admin/areas/${areaId}/plates/${plateId}`)
      close()
    })

  /** legacy .buycurinvoce → afterpayment → модалка «Подтверждение оплаты». */
  const openPay = (invoice: InvoiceRow): Promise<void> =>
    run(async () => {
      const after = await api.get<AfterPayment>(
        `/api/admin/areas/${areaId}/invoices/${invoice.smsid}/after-payment`,
      )
      setModal({
        kind: 'pay',
        invoice,
        datee: invoiceDate(invoice),
        rest: after.rest,
        canPay: after.can_pay,
      })
    })

  const payInvoice = (invoice: InvoiceRow): Promise<void> =>
    run(async () => {
      await api.post(`/api/admin/areas/${areaId}/invoices/${invoice.smsid}/pay`, {
        type: invoice.typeinvoice,
      })
      close()
    })

  /**
   * legacy .autoinvoicespay: при включении с неоплаченными квитанциями — confirm()
   * и только затем setautoinvoicespay (бекенд сам гасит что может).
   */
  function toggleAutopay(value: boolean): void {
    if (value && data !== null && data.unpaid_count > 0) {
      const ok = window.confirm(
        'Имеются не оплаченые квитанции, желаете активировать автооплату? Все квитанции будут оплачены автоматически при наличии средств на счету',
      )
      if (!ok) return
    }
    void run(async () => {
      await api.post(`/api/admin/areas/${areaId}/autopay`, { value: String(value) })
    })
  }

  // ── Разметка ──────────────────────────────────────────────────────────────

  return (
    <>
      <div onClick={() => window.history.go(-1)} className="areabackbtn">
        Назад
      </div>
      <h2 className="sub-header">
        Настройки участка №<span className="areainfonbm">{areaNbm}</span>
      </h2>
      <div className="curareaid" data-areaid={areaId} />

      <div className="modalsblock">
        {modal.kind === 'editInfo' && (
          <>
            <div className="removemodalbk" onClick={onBackdropClick} />
            <div className="removemodal">
              <div className="admtitle">
                <div className="admname">Редактирование</div>
                <div className="admclose" onClick={close}>
                  x
                </div>
              </div>
              <div className="admbody">
                <input
                  type="text"
                  className="adminfoedit"
                  value={infoValue}
                  disabled={modal.type === 'infopassword'}
                  onChange={(event) => setInfoValue(event.target.value)}
                />
              </div>
              <div className="admfoot">
                <div
                  className={`edit${modal.type} editareainfo`}
                  data-type={modal.type}
                  onClick={() => void saveInfo(modal.type)}
                >
                  {modal.type === 'infopassword' ? 'Сгенерировать' : 'Сохранить'}
                </div>
              </div>
            </div>
          </>
        )}

        {modal.kind === 'syncArea' && (
          <>
            <div className="addnewsyncareamodalbk" onClick={onBackdropClick} />
            <div className="addnewsyncareamodal">
              <div className="admtitle">
                <div className="admname">Прикрепить участок</div>
                <div className="admclose" onClick={close}>
                  x
                </div>
              </div>
              <div className="admbody">
                <table className="clbsyncareastable">
                  <thead>
                    <tr className="clbsyncareaitemth">
                      <th className="syncareaname">Связанный участок</th>
                      <th className="syncareacontrol">Удаление</th>
                    </tr>
                  </thead>
                  <tbody className="bodysyncareastable">
                    {synced.map((row) => (
                      <tr key={row.id} className="clbsyncareaitem">
                        <td className="syncareaname">Участок {row.name}</td>
                        <td className="syncareacontrol">
                          <div className="syncareacontrolflex">
                            <div
                              className="syncarearemove"
                              data-syncarea={row.id}
                              onClick={() => setModal({ kind: 'confirmSyncDel', syncId: row.id })}
                            >
                              <i className="far fa-trash-alt" />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="footsyncareastable" />
                </table>
                <div className="admbsyncarea">
                  <select
                    name="admbsyncareaselect"
                    className="admbsyncareaselect"
                    data-live-search="true"
                    data-size="10"
                    data-width="100%"
                    value={selected}
                    onChange={(event) => setSelected(event.target.value)}
                  >
                    <option value="0">Выберите участок</option>
                    {modal.candidates.map((row) => (
                      <option key={row.id} value={row.id}>
                        Участок {row.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="admfoot">
                <div className="admareasyncaddbtn" onClick={() => void addSyncArea()}>
                  Добавить
                </div>
              </div>
            </div>
          </>
        )}

        {modal.kind === 'confirmSyncDel' && (
          <>
            <div className="removemodalbk" onClick={onBackdropClick} />
            <div className="removemodal">
              <div className="admtitle">
                <div className="admname">Подтверждение удаления</div>
                <div className="admclose" onClick={close}>
                  x
                </div>
              </div>
              <div className="admbody">Подтвердить удаление участка</div>
              <div className="admfoot">
                <div
                  className="syncareadel"
                  data-syncarea={modal.syncId}
                  onClick={() => void delSyncArea(modal.syncId)}
                >
                  Удалить
                </div>
              </div>
            </div>
          </>
        )}

        {modal.kind === 'addCounter' && (
          <>
            <div className="addnewcountermodalbk" onClick={onBackdropClick} />
            <div className="addnewcountermodal">
              <div className="admtitle">
                <div className="admname">Добавить счетчик</div>
                <div className="admclose" onClick={close}>
                  x
                </div>
              </div>
              <div className="admbody">
                <div className="admbcounter">
                  <select
                    name="admbcounterselect"
                    className="admbcounterselect"
                    data-live-search="true"
                    data-size="10"
                    data-width="100%"
                    value={selected}
                    onChange={(event) => setSelected(event.target.value)}
                  >
                    {modal.free.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.number}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="admfoot">
                <div className="admfaddbtn" onClick={() => void addCounter()}>
                  Добавить
                </div>
              </div>
            </div>
          </>
        )}

        {modal.kind === 'confirmCounterDel' && (
          <>
            <div className="removemodalbk" onClick={onBackdropClick} />
            <div className="removemodal">
              <div className="admtitle">
                <div className="admname">Подтверждение удаления</div>
                <div className="admclose" onClick={close}>
                  x
                </div>
              </div>
              <div className="admbody">Подтвердить удаление счетчика</div>
              <div className="admfoot">
                <div
                  className="delcounter"
                  data-counter={modal.counterId}
                  onClick={() => void delCounter(modal.counterId)}
                >
                  Удалить
                </div>
              </div>
            </div>
          </>
        )}

        {modal.kind === 'contactForm' && (
          <>
            <div className="addnewusermodalbk" onClick={onBackdropClick} />
            <div className={modal.owner ? 'addnewusermodal' : 'addnewusermodal addnewsmall'}>
              <div className="admtitle">
                <div className="admname">{modal.title}</div>
                <div className="admclose" onClick={close}>
                  x
                </div>
              </div>
              <div className="admbody">
                <div className="admfio">
                  <input
                    type="text"
                    className="adminputfamilia"
                    placeholder="Фамилия"
                    value={contact.familia}
                    onChange={(event) => setContact({ ...contact, familia: event.target.value })}
                  />
                  <input
                    type="text"
                    className="adminputimuy"
                    placeholder="Имя"
                    value={contact.imuy}
                    onChange={(event) => setContact({ ...contact, imuy: event.target.value })}
                  />
                  <input
                    type="text"
                    className="adminputotchestvo"
                    placeholder="Отчество"
                    value={contact.otchestvo}
                    onChange={(event) => setContact({ ...contact, otchestvo: event.target.value })}
                  />
                </div>

                <div className="admtel">
                  <input
                    type="text"
                    className="adminputtel"
                    placeholder="Телефон"
                    value={contact.tel}
                    onChange={(event) => setContact({ ...contact, tel: event.target.value })}
                  />
                  {/* Чекбокс SMS убран: канал отключён. Значение rassl_sms сохраняется как
                      есть (contact.sms сидируется из строки и уходит при сохранении) —
                      колонку читает legacy-сайт на той же базе. */}
                </div>
                <div className="admemail">
                  <input
                    type="text"
                    className="adminputemail"
                    placeholder="Email"
                    value={contact.email}
                    onChange={(event) => setContact({ ...contact, email: event.target.value })}
                  />
                  {modal.owner && (
                    <label htmlFor="admcheckboxemail">
                      <input
                        type="checkbox"
                        className="admcheckboxemail"
                        id="admcheckboxemail"
                        checked={contact.emailRassl}
                        onChange={(event) =>
                          setContact({ ...contact, emailRassl: event.target.checked })
                        }
                      />
                      Email
                    </label>
                  )}
                </div>
                {modal.owner && (
                  <div className="admcandidate">
                    <label htmlFor="admcheckboxcandidate">
                      <input
                        type="checkbox"
                        className="admcheckboxcandidate"
                        id="admcheckboxcandidate"
                        checked={contact.candidate}
                        onChange={(event) =>
                          setContact({ ...contact, candidate: event.target.checked })
                        }
                      />
                      Кандидат
                    </label>
                  </div>
                )}
              </div>
              <div className="admfoot">
                {modal.userId === null ? (
                  <div
                    className="admfaddbtnusertoarea"
                    data-typeadd={modal.owner ? 'addowner' : undefined}
                    onClick={() => void addContact(modal.owner)}
                  >
                    {modal.owner ? 'Сохранить' : 'Добавить'}
                  </div>
                ) : (
                  <div
                    className="admfeditbtnusertoarea"
                    data-userid={modal.userId}
                    onClick={() => void editContact(modal.userId ?? 0)}
                  >
                    Сохранить
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {modal.kind === 'confirmContactDel' && (
          <>
            <div className="removemodalbk" onClick={onBackdropClick} />
            <div className="removemodal">
              <div className="admtitle">
                <div className="admname">Подтверждение удаления</div>
                <div className="admclose" onClick={close}>
                  x
                </div>
              </div>
              <div className="admbody">Подтвердить удаление контакта</div>
              <div className="admfoot">
                <div
                  className="delcontact"
                  data-userid={modal.userId}
                  onClick={() => void delContact(modal.userId)}
                >
                  Удалить
                </div>
              </div>
            </div>
          </>
        )}

        {modal.kind === 'contactDelBlocked' && (
          <>
            <div className="removemodalbk" onClick={onBackdropClick} />
            <div className="removemodal">
              <div className="admtitle">
                <div className="admname">Подтверждение удаления</div>
                <div className="admclose" onClick={close}>
                  x
                </div>
              </div>
              <div className="admbody">
                Удаление основного контакта не доступно при наличии других контактов, счетчиков и
                номеров автомобилей.
              </div>
            </div>
          </>
        )}

        {modal.kind === 'plateForm' && (
          <>
            <div className="avtoplatemodalbk" onClick={onBackdropClick} />
            <div className="avtoplatemodal">
              <div className="admtitle">
                <div className="admname">
                  {modal.plateId === null
                    ? 'Добавить номер автомобиля'
                    : 'Редактировать номер автомобиля'}
                </div>
                <div className="admclose" onClick={close}>
                  x
                </div>
              </div>
              <div className="admbody">
                <div className="avtoplatenumber">
                  <input
                    type="text"
                    className="avtoplatenumberinput"
                    value={plate.number}
                    onChange={(event) => setPlate({ ...plate, number: event.target.value })}
                  />
                  <input
                    type="text"
                    className="avtoplateregioninput"
                    value={plate.region}
                    onChange={(event) => setPlate({ ...plate, region: event.target.value })}
                  />
                </div>
              </div>
              <div className="admfoot">
                {modal.plateId === null ? (
                  <div className="addavtoplate" onClick={() => void savePlate(null)}>
                    Добавить
                  </div>
                ) : (
                  <div
                    className="editavtoplate"
                    data-plateid={modal.plateId}
                    onClick={() => void savePlate(modal.plateId)}
                  >
                    Сохранить
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {modal.kind === 'confirmPlateDel' && (
          <>
            <div className="removemodalbk" onClick={onBackdropClick} />
            <div className="removemodal">
              <div className="admtitle">
                <div className="admname">Подтверждение удаления</div>
                <div className="admclose" onClick={close}>
                  x
                </div>
              </div>
              <div className="admbody">Подтвердить удаление номера автомобиля</div>
              <div className="admfoot">
                <div
                  className="delavtoplate"
                  data-plateid={modal.plateId}
                  onClick={() => void delPlate(modal.plateId)}
                >
                  Удалить
                </div>
              </div>
            </div>
          </>
        )}

        {modal.kind === 'pay' && (
          <div className="alertPaymentModal">
            <div className="modalheader">
              <div className="headertitle">Подтверждение оплаты</div>
              <div className="headerclose" onClick={close}>
                X
              </div>
            </div>
            <div className="modalbody">
              <table>
                <tbody>
                  <tr>
                    <td>Тип:</td>
                    <td className="pl10">{INVOICE_TYPE[modal.invoice.typeinvoice]}</td>
                  </tr>
                  <tr>
                    <td>Дата:</td>
                    <td className="pl10">{modal.datee}</td>
                  </tr>
                  <tr>
                    <td>Сумма:</td>
                    <td className="pl10">{modal.invoice.totalcost} руб.</td>
                  </tr>
                  {modal.canPay && (
                    <tr>
                      <td>Баланс после оплаты:</td>
                      <td className="pl10">{modal.rest} руб.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {!modal.canPay && (
                <p
                  style={{
                    paddingTop: 30,
                    color: '#c40000',
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                >
                  На счете недостаточно средств для оплаты
                </p>
              )}
            </div>
            <div className="modalfooter">
              {modal.canPay && (
                <button
                  className="accept_paymentg"
                  data-type={modal.invoice.typeinvoice}
                  data-invoiceid={modal.invoice.smsid}
                  data-sum={modal.invoice.totalcost}
                  data-areaid={areaId}
                  onClick={() => void payInvoice(modal.invoice)}
                >
                  Подтвердить оплату
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {failure !== null && (
        <div className="infobox clearfix">
          <p className="okke" style={{ color: '#ce4844', float: 'left' }}>
            {failure}
          </p>
        </div>
      )}

      <div className="areainfo col-lg-9 col-md-12">
        <table className="areainfotable">
          <tbody>
            <tr>
              <td className="areanname">Номер участка</td>
              <td className="areanvalue">
                <span className="areainfonbm">{areaNbm}</span>
              </td>
              <td className="areancontrol">
                <div className="areancontrolflex">
                  <div className="syncareaadd" onClick={() => void openSyncArea()}>
                    <i className="far fa-edit" />
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="areanname">№ лицевого счёта</td>
              <td className="areanvalue">
                <span className="areainfopid">{area.area_pid}</span>
              </td>
              <td className="areancontrol" />
            </tr>
            <tr>
              <td className="areanname">Площадь участка кв.м</td>
              <td className="areanvalue">
                <span className="areainfoland">{area.land_area}</span>
              </td>
              <td className="areancontrol">
                <div className="areancontrolflex">
                  <div
                    className="areancontroledit"
                    data-type="infoland"
                    onClick={() => openEditInfo('infoland')}
                  >
                    <i className="far fa-edit" />
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="areanname">Ставка за 1 кв.м</td>
              <td className="areanvalue">
                <span className="areainfolandrate">{area.land_area_rate} руб.</span>
              </td>
              <td className="areancontrol">
                <div className="areancontrolflex">
                  <div
                    className="areancontroledit"
                    data-type="infolandrate"
                    onClick={() => openEditInfo('infolandrate')}
                  >
                    <i className="far fa-edit" />
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="areanname">Баланс</td>
              <td className="areanvalue">
                <span className="areainfomoney">{area.money} руб.</span>
              </td>
              <td className="areancontrol" />
            </tr>
            <tr>
              <td className="areanname">Логин</td>
              <td className="areanvalue">
                <span className="areainfologin">{area.login}</span>
              </td>
              <td className="areancontrol">
                <div className="areancontrolflex">
                  <div
                    className="areancontroledit"
                    data-type="infologin"
                    onClick={() => openEditInfo('infologin')}
                  >
                    <i className="far fa-edit" />
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="areanname">Пароль</td>
              <td className="areanvalue">
                <span className="areainfopassword">{area.password}</span>
              </td>
              <td className="areancontrol">
                <div className="areancontrolflex">
                  <div
                    className="areancontroledit"
                    data-type="infopassword"
                    onClick={() => openEditInfo('infopassword')}
                  >
                    <i className="far fa-edit" />
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="areanname">Автооплата квитанций</td>
              <td className="areanvalue">
                <label className="switch">
                  <input
                    type="checkbox"
                    className="autoinvoicespay"
                    checked={isTrue(area.invoiceautopay)}
                    onChange={(event) => toggleAutopay(event.target.checked)}
                  />
                  <span className="slider" />
                </label>
              </td>
              <td className="areancontrol" />
            </tr>
          </tbody>
        </table>
      </div>

      <div className="maincontact col-lg-9 col-md-12">
        <div className="clhead" style={headStyle('maincontact')}>
          <div
            className="clhtitle clcollapse"
            data-cltype="cl"
            onClick={() => toggleSection('maincontact')}
          >
            Основной контакт {collapseIcon('maincontact')}
          </div>
          {/* legacy initmainuser: кнопку «Добавить» прячем, когда собственник уже есть */}
          {!hasOwner && (
            <div className="clhmaincontactadd" onClick={() => openAddContact(true)}>
              Добавить
            </div>
          )}
        </div>
        <div className="clbody" style={bodyStyle('maincontact')}>
          <table className="clbcontactsеtable">
            <thead>
              <tr className="clbcontactitemth">
                <th className="contactnbm">№</th>
                <th className="contactname">ФИО</th>
                <th className="contacttel">Телефон</th>
                <th className="contactemail">Email</th>
                <th className="contactcontrole">Управление</th>
              </tr>
            </thead>
            <tbody className="bodymaincontactsеtable">
              {owner === null ? (
                <tr className="clbcontactitem">
                  <td colSpan={3} align="center">
                    Контакты отсутствуют.
                  </td>
                </tr>
              ) : (
                <tr className="clbcontactitem">
                  <td className="contactnbm">1</td>
                  <td className="contactname">{fio(owner)}</td>
                  <td className="contacttel">{owner.tel}</td>
                  <td className="contactemail">{owner.email ?? ''}</td>
                  <td className="contactcontrole">
                    <div className="contactcontroleflex">
                      <div
                        className="contactedit"
                        data-userid={owner.id}
                        data-notowner="false"
                        onClick={() => void openEditContact(owner.id, false)}
                      >
                        <i className="fas fa-user-cog" />
                      </div>
                      <div
                        className="contactremove"
                        data-userid={owner.id}
                        data-contacttype="main"
                        onClick={() => openDelContact(owner.id, 'main')}
                      >
                        <i className="fas fa-user-times" />
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="footmaincontactsеtable" />
          </table>
        </div>
      </div>

      {/* legacy initmainuser: блоки ниже показываются только при наличии основного контакта */}
      {hasOwner && (
        <>
          <div className="contactslist col-lg-9 col-md-12">
            <div className="clhead" style={headStyle('contacts')}>
              <div
                className="clhtitle clcollapse"
                data-cltype="cl"
                onClick={() => toggleSection('contacts')}
              >
                Доп. Контакты {collapseIcon('contacts')}
              </div>
              <div className="clhcontactadd" onClick={() => openAddContact(false)}>
                Добавить
              </div>
            </div>
            <div className="clbody" style={bodyStyle('contacts')}>
              <table className="clbcontactsеtable">
                <thead>
                  <tr className="clbcontactitemth">
                    <th className="contactnbm">№</th>
                    <th className="contactname">ФИО</th>
                    <th className="contacttel">Телефон</th>
                    <th className="contactemail">Email</th>
                    <th className="contactcontrole">Управление</th>
                  </tr>
                </thead>
                <tbody className="bodycontactsеtable">
                  {contacts.map((row, index) => (
                    <tr key={row.id} className="clbcontactitem">
                      <td className="contactnbm">{index + 1}</td>
                      <td className="contactname">{fio(row)}</td>
                      <td className="contacttel">{row.tel}</td>
                      <td className="contactemail">{row.email ?? ''}</td>
                      <td className="contactcontrole">
                        <div className="contactcontroleflex">
                          <div
                            className="contactedit"
                            data-userid={row.id}
                            data-notowner="true"
                            onClick={() => void openEditContact(row.id, true)}
                          >
                            <i className="fas fa-user-cog" />
                          </div>
                          <div
                            className="contactremove"
                            data-userid={row.id}
                            data-contacttype="other"
                            onClick={() => openDelContact(row.id, 'other')}
                          >
                            <i className="fas fa-user-times" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="footcontactsеtable" />
              </table>
            </div>
          </div>

          <div className="counterslist col-lg-9 col-md-12">
            <div className="clhead" style={headStyle('counters')}>
              <div
                className="clhtitle clcollapse"
                data-cltype="cl"
                onClick={() => toggleSection('counters')}
              >
                Счетчики {collapseIcon('counters')}
              </div>
              <div className="clhcounteradd" onClick={() => void openAddCounter()}>
                Прикрепить
              </div>
            </div>
            <div className="clbody" style={bodyStyle('counters')}>
              <table className="clbcounterstable">
                <thead>
                  <tr className="clbcontactitemth">
                    <th className="counternbm">№</th>
                    <th className="countername">Номер счетчика</th>
                    <th className="countercontrol">Управление</th>
                  </tr>
                </thead>
                <tbody className="bodycounterstable">
                  {counters.map((row, index) => (
                    <tr key={row.id} className="clbcontactitem">
                      <td className="counternbm">{index + 1}</td>
                      <td className="countername">{row.number}</td>
                      <td className="countercontrol">
                        <div className="countercontrolflex">
                          <div
                            className="counterremove"
                            data-counter={row.id}
                            onClick={() =>
                              setModal({ kind: 'confirmCounterDel', counterId: row.id })
                            }
                          >
                            <i className="far fa-trash-alt" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="footcounterstable" />
              </table>
            </div>
          </div>

          <div className="avtoplateslist col-lg-9 col-md-12">
            <div className="clhead" style={headStyle('plates')}>
              <div
                className="clhtitle clcollapse"
                data-cltype="cl"
                onClick={() => toggleSection('plates')}
              >
                Номера автомобилей {collapseIcon('plates')}
              </div>
              <div className="clhavtoplateadd" onClick={openAddPlate}>
                Добавить
              </div>
            </div>
            <div className="clbody" style={bodyStyle('plates')}>
              <table className="clbavtoplatestable">
                <thead>
                  <tr className="clbavtoplateitemth">
                    <th className="avtoplatenbm">№</th>
                    <th className="avtoplatename">Номер</th>
                    <th className="avtoplatecontrol">Управление</th>
                  </tr>
                </thead>
                <tbody className="bodyavtoplatestable">
                  {plates.length === 0 ? (
                    <tr className="clbavtoplateitem">
                      <td colSpan={3} align="center">
                        Номера отсутствуют.
                      </td>
                    </tr>
                  ) : (
                    plates.map((row, index) => (
                      <tr key={row.id} className="clbavtoplateitem">
                        <td className="avtoplatenbm">{index + 1}</td>
                        <td className="avtoplatename">
                          {row.avtoplatenumber} {row.avtoplateregion}
                        </td>
                        <td className="avtoplatecontrole">
                          <div className="avtoplatecontroleflex">
                            <div
                              className="avtoplateedit"
                              data-plateid={row.id}
                              onClick={() => void openEditPlate(row.id)}
                            >
                              <i className="fas fa-user-cog" />
                            </div>
                            <div
                              className="avtoplateremove"
                              data-plateid={row.id}
                              onClick={() => setModal({ kind: 'confirmPlateDel', plateId: row.id })}
                            >
                              <i className="fas fa-user-times" />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="footavtoplatestable" />
              </table>
            </div>
          </div>

          <div className="invoicesslist col-lg-9 col-md-12">
            <div className="invoicessheadblock nclhead" style={headStyle('invoices')}>
              <div
                className="hhtitle clcollapse"
                data-cltype="ncl"
                onClick={() => toggleSection('invoices')}
              >
                Выставленные квитанции {collapseIcon('invoices')}
              </div>
              <div className="hhlasttransactions" />
              <div className="hhlasttransactions" />
            </div>
            <div className="invoicessbodyblock nclbody" style={bodyStyle('invoices')}>
              <table className="invosesstable">
                <thead>
                  <tr className="bodylistinvoicessitem">
                    <th className="invoicessnbm">№</th>
                    <th className="invoicessdata">Дата</th>
                    <th className="invoicessdata">Участок</th>
                    <th className="invoicesssumm">Сумма</th>
                    <th className="invoicesskvit">Квитанция</th>
                    <th className="invoicesstype">Тип</th>
                    <th className="invoicesstatus">Статус</th>
                  </tr>
                </thead>
                <tbody className="bodylistinvocesitembl">
                  {invoices.length === 0 ? (
                    <tr className="bodylistinvoicessitem">
                      <td colSpan={6} align="center">
                        Неоплаченные квитанции отсутствуют.
                      </td>
                    </tr>
                  ) : (
                    invoices.map((row, index) => (
                      <tr key={row.smsid} className="bodylistinvoicessitem">
                        <td className="invoicessnbm">{index + 1}</td>
                        <td className="invoicessdata">{invoiceDate(row)}</td>
                        <td className="invoicessdata">№{row.areaname}</td>
                        <td className="invoicesssumm">{row.totalcost} руб.</td>
                        <td className="invoicesskvit">
                          <a
                            href={`/getpdfinvoice?invid=${row.smsid}&type=${row.typeinvoice}&area=${row.areaid ?? areaId}`}
                            target="_blank"
                            rel="noreferrer"
                            // Админ качает квитанцию с Bearer-токеном: по обычной ссылке
                            // браузер заголовок не пошлёт. ?area обязателен — админ смотрит
                            // чужой участок, и бекенд берёт участок именно оттуда.
                            onClick={(event) => {
                              event.preventDefault()
                              void api.downloadFile(
                                `/getpdfinvoice?invid=${row.smsid}&type=${row.typeinvoice}&area=${row.areaid ?? areaId}`,
                                `Квитанция ${row.smsid}.pdf`,
                                true,
                              )
                            }}
                          >
                            <i className="far fa-file-pdf" />
                          </a>
                        </td>
                        <td className="invoicesstype">{INVOICE_TYPE[row.typeinvoice]}</td>
                        <td className="invoicesstatus">
                          {isTrue(row.oldinv) ? null : isTrue(row.waspaid) ? (
                            'Оплачено'
                          ) : (
                            <button
                              className="buycurinvoce lkbuytinv"
                              data-type={row.typeinvoice}
                              data-invoiceid={row.smsid}
                              data-sum={row.totalcost}
                              data-areaid={areaId}
                              onClick={() => void openPay(row)}
                            >
                              Оплатить
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="footlistinvocesitembl" />
              </table>
            </div>
            <div className="invoicesslistaddarrow" />
          </div>

          <div className="historylist col-lg-9 col-md-12">
            <div className="historyheadblock nclhead" style={headStyle('history')}>
              <div
                className="hhtitle clcollapse"
                data-cltype="ncl"
                onClick={() => toggleSection('history')}
              >
                История по счету {collapseIcon('history')}
              </div>
              <div className="hhlasttransactions" />
            </div>
            <div className="historybodyblock nclbody" style={bodyStyle('history')}>
              <table className="invosesstable">
                <thead>
                  <tr className="bodylisthistoryitem">
                    <th className="historynbm">№</th>
                    <th className="historydata">Дата</th>
                    <th className="historydata">Участок</th>
                    <th className="historysumm">Сумма</th>
                    <th className="historytype">Тип</th>
                  </tr>
                </thead>
                <tbody className="bodylisthistoryitembl">
                  {transactions.length === 0 ? (
                    <tr className="bodylisthistoryitem">
                      <td colSpan={5} align="center">
                        Нет транзакций по счету.
                      </td>
                    </tr>
                  ) : (
                    transactions.map((row, index) => (
                      <tr key={row.id} className="bodylisthistoryitem">
                        <td className="historynbm">{index + 1}</td>
                        <td className="historydata">{dateNormalizeWithTime(row.time)}</td>
                        <td className="historydata">
                          {row.areaname !== null ? `№${row.areaname}` : ''}
                        </td>
                        <td className="historysumm">{row.sum} руб.</td>
                        <td className="historytype">{row.name}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="footlisthistoryitembl" />
              </table>
            </div>
            <div className="historylistaddarrow" />
          </div>
        </>
      )}
    </>
  )
}
